import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useCampaignSettingsMetaAdsStore } from '@/store/campaignSettingsMetaAdsStore';

/**
 * Phase 0.5 — Cross-platform currency guard.
 *
 * Walks the flouri-optimized campaign pool across BOTH the Google and Meta
 * settings stores, looks up each campaign's account currency via the
 * already-preloaded `authStore.linkedAccountDetails` /
 * `linkedMetaAccountDetails` (currency captured at link time per the
 * Phase 0.5 MCC fix + Meta accounts-summary plumbing), and returns the
 * pool's currency posture.
 *
 * Consumers (`BudgetOptimizerSimple`) gate the Optimize buttons on
 * `isMixed === false`, and pass `currency` as the `currency_code` field
 * on the `/optimization/budget-allocation` request body. The backend
 * runs its own per-campaign validation as defense-in-depth — see
 * `_validate_campaign_currencies` in `src/api/routers/optimization.py`.
 *
 * Behavior:
 * - `currency` is the single ISO code shared by all pool campaigns.
 *   `null` when the pool is empty OR genuinely mixed.
 * - `isMixed` is true when the pool contains campaigns whose account
 *   currencies disagree (≥2 distinct non-null currencies).
 * - `currenciesInPool` lists every distinct currency observed in the
 *   pool, in the order encountered (used by `BudgetOptimizerSimple`'s
 *   alert copy: "You have campaigns in USD, EUR").
 *
 * Edge cases:
 * - Campaigns whose account currency is `null` (legacy pre-Phase-0.5
 *   credential rows) are silently skipped — they don't count toward
 *   mixedness, but they also don't establish the pool's currency.
 *   Operator fix: re-link the account to repopulate the column.
 * - Campaigns whose account_id doesn't resolve to a linked account
 *   (stale local state, race against logout) are also skipped.
 */

export interface PoolCurrencyResult {
  /** The single ISO 4217 currency shared by every flouri-optimized campaign in
   *  the pool, or `null` when the pool is empty / mixed / all-unknown. */
  currency: string | null;
  /** True when the pool spans ≥2 distinct currencies. Drives the Alert
   *  + Optimize-button disable in `BudgetOptimizerSimple`. */
  isMixed: boolean;
  /** Distinct currencies present in the pool (in encounter order).
   *  Empty array when the pool is empty OR every campaign's currency
   *  resolved to `null` (no usable credential). */
  currenciesInPool: string[];
}

export function usePoolCurrency(): PoolCurrencyResult {
  const linkedGoogleAccounts = useAuthStore((s) => s.linkedAccountDetails);
  const linkedMetaAccounts = useAuthStore((s) => s.linkedMetaAccountDetails);
  const googleCampaignSettings = useCampaignSettingsGoogleAdsStore((s) => s.campaignSettings);
  const metaCampaignSettings = useCampaignSettingsMetaAdsStore((s) => s.campaignSettings);

  return useMemo(() => {
    // Build account_id → currency lookups for both platforms.
    // Google IDs are bare digits; Meta IDs include the 'act_' prefix
    // (see `meta_ad_account_id` shape in `meta_validators.py`). The
    // currency lookups stay per-platform so we don't accidentally
    // cross-match a numeric collision.
    const googleCurrencyByAccount = new Map<string, string>();
    for (const acc of linkedGoogleAccounts) {
      if (acc.currencyCode) {
        googleCurrencyByAccount.set(acc.id, acc.currencyCode);
      }
    }
    const metaCurrencyByAccount = new Map<string, string>();
    for (const acc of linkedMetaAccounts) {
      if (acc.currencyCode) {
        metaCurrencyByAccount.set(acc.id, acc.currencyCode);
      }
    }

    // Walk the pool — only flouri-optimized campaigns count. Status
    // doesn't matter here (the simplex itself filters on status); the
    // currency-mixedness check is about declared intent, so a paused
    // campaign with flouri_optimize_budget=true still contributes.
    const currenciesInPool: string[] = [];
    const seen = new Set<string>();
    const observe = (ccy: string | undefined) => {
      if (!ccy) return;
      if (seen.has(ccy)) return;
      seen.add(ccy);
      currenciesInPool.push(ccy);
    };

    for (const settings of googleCampaignSettings.values()) {
      if (!settings.isOptimizationEnabled) continue;
      if (!settings.googleAdsAccountId) continue;
      observe(googleCurrencyByAccount.get(settings.googleAdsAccountId));
    }
    for (const settings of metaCampaignSettings.values()) {
      if (!settings.isOptimizationEnabled) continue;
      if (!settings.metaAdAccountId) continue;
      observe(metaCurrencyByAccount.get(settings.metaAdAccountId));
    }

    const isMixed = currenciesInPool.length > 1;
    const currency =
      currenciesInPool.length === 1 ? currenciesInPool[0] : null;

    return { currency, isMixed, currenciesInPool };
  }, [
    linkedGoogleAccounts,
    linkedMetaAccounts,
    googleCampaignSettings,
    metaCampaignSettings,
  ]);
}
